import { useMutation } from '@apollo/client';
import { FirebaseError } from '@firebase/util';
import { isEmpty, kebabCase, omit } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { InferType } from 'yup';

import { useAppContext } from '../../../../data/context/app-context';
import { useGenderValues } from '../../../../data/data/select-values';
import { toUpload } from '../../../../data/GraphQL/api/format';
import {
  CurrentHealthProfessionalItemFragment,
  Gender,
  Language,
  UpdateCurrentHealthProfessionalDocument,
} from '../../../../data/types/api/graphql';
import { formatDate, maxBirthDate } from '../../../../data/utils/date.utils';
import {
  isEmptyOrNone,
  useFormatters,
} from '../../../../data/utils/formatters.utils';
import {
  FileSizeImage,
  FileTypeImage,
  getFileDataFromUrl,
} from '../../../../data/utils/upload.utils';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import { AvatarUploadController } from '../../../../elements/avatar/avatar-upload/controllers/avatar-upload';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { InputCheckboxController } from '../../../../elements/form/controllers/input-checkbox';
import { InputFieldController } from '../../../../elements/form/controllers/input-field';
import { FormColumns, FormRow } from '../../../../elements/form/form';
import { BaseInputIcon } from '../../../../elements/form/input/base-input';
import { InputCheckboxType } from '../../../../elements/form/input/input-checkbox';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';
import { PincodeInput } from '../../../../elements/pincode-input/pincode-input';
import { CopyText } from '../../../../elements/utils/copy-text/copy-text';
import {
  AuthStatus,
  IAuthenticationStatus,
} from '../../../../firebase/authentication';
import { schemas } from './form/schemas';
import { ProfileFormAction, useProfileForm } from './form/use-profile-form';

export enum ProfileModalAction {
  DEFAULT = 'DEFAULT',
  UPDATE = 'UPDATE',
  UPDATEPASSWORD = 'UPDATE_PASSWORD',
  UPDATEPINCODE = 'UPDATE_PINCODE',
}

type ProfileModalProps = {
  action?: ProfileModalAction;
  onSaved?: (patient: CurrentHealthProfessionalItemFragment) => void;
  onCancel?: () => void;
  onClose?: () => void;
};

export function ProfileModal({
  onSaved,
  onCancel,
  onClose,
  ...props
}: ProfileModalProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const {
    user,
    reloadUser,
    verifyWithPassword,
    setPassword,
    verifySMSCode,
    setPinCode,
  } = useAppContext();

  const [action, setAction] = useState<ProfileModalAction>(
    ProfileModalAction.DEFAULT
  );

  useEffect(() => {
    setAction(props.action ?? ProfileModalAction.DEFAULT);
  }, [props.action]);

  const { form, formIsValid, setErrors } = useProfileForm(
    ([
      ProfileModalAction.UPDATEPASSWORD,
      ProfileModalAction.UPDATEPINCODE,
    ].includes(action)
      ? action
      : ProfileModalAction.DEFAULT) as unknown as ProfileFormAction
  );

  useEffect(() => {
    if (action === ProfileModalAction.UPDATEPASSWORD) {
      form.reset();
    }

    if (action === ProfileModalAction.UPDATEPASSWORD) {
      form.reset();
    }

    if (action === ProfileModalAction.DEFAULT) {
      if (user) {
        form.reset(omit(user, 'avatar'));

        (async () => {
          form.setValue(
            'avatar',
            user.avatar ? await getFileDataFromUrl(user.avatar) : null
          );
        })();
      }
    }
  }, [form, action, user]);

  const { data: genderData } = useGenderValues();

  const [loading, setLoading] = useState<boolean>(false);

  const [passwordStatus, setPasswordStatus] = useState<{
    status?: IAuthenticationStatus | null;
    error?: string | null;
  } | null>(null);

  useEffect(() => {
    setPasswordStatus(null);
  }, [action]);

  const newPassword = form.watch('newPassword');
  const repeatNewPassword = form.watch('repeatNewPassword');
  useEffect(() => {
    if (
      !isEmpty(repeatNewPassword) &&
      !isEmpty(newPassword) &&
      repeatNewPassword !== newPassword
    ) {
      form.trigger(['repeatNewPassword', 'newPassword']);
    }
  }, [form, newPassword, repeatNewPassword]);

  const onHandleCode = useCallback(
    async (code: string) => {
      setLoading(true);

      setPasswordStatus(prevState => ({
        ...(prevState ?? {}),
        error: null,
      }));

      try {
        if (
          passwordStatus?.status?.verification &&
          passwordStatus?.status?.status === AuthStatus.UNVERIFIED
        ) {
          const result = await verifySMSCode(
            code,
            passwordStatus?.status?.verification
          );

          if (result.status === AuthStatus.VERIFIED) {
            const passwordResult = await setPassword(newPassword ?? '');

            if (passwordResult.success) {
              showAlertMessage({
                type: AlertMessageType.SUCCESS,
                message: intl.formatMessage({
                  id: 'profile.update-password.success',
                }),
              });

              onSaved?.(user!);

              setAction(ProfileModalAction.DEFAULT);
            } else {
              showAlertMessage({
                type: AlertMessageType.ERROR,
                message: passwordResult?.message ?? '',
              });
            }
          } else if (
            result.status === AuthStatus.UNVERIFIED &&
            result.message
          ) {
            setPasswordStatus(prevState => ({
              ...(prevState ?? {}),
              error: `common.errors.${kebabCase(
                result.message ?? 'unknown-error'
              )}`,
            }));
          }

          setPasswordStatus(prevState => ({
            ...(prevState ?? {}),
            status: result,
          }));
        } else {
          showAlertMessage({
            type: AlertMessageType.ERROR,
            message: passwordStatus?.status?.message ?? '',
          });
        }
      } catch (error) {
        if (error instanceof FirebaseError) {
          showAlertMessage({
            type: AlertMessageType.ERROR,
            message: intl.formatMessage({
              id: `common.errors.${kebabCase(error.code ?? 'unknown-error')}`,
              defaultMessage: error.code ?? 'unknown-error',
            }),
          });
        } else {
          showAlertMessage({
            type: AlertMessageType.ERROR,
            message: (error as Error).message,
          });
        }
      }

      setLoading(false);
    },
    [
      intl,
      user,
      passwordStatus,
      newPassword,
      setPassword,
      verifySMSCode,
      showAlertMessage,
      onSaved,
    ]
  );

  const [profileUpdate] = useMutation(UpdateCurrentHealthProfessionalDocument);

  const onHandleSubmit = useCallback(() => {
    form.handleSubmit(async () => {
      setLoading(true);

      if (action === ProfileModalAction.UPDATEPASSWORD) {
        const values = form.getValues() as InferType<typeof schemas.password>;

        try {
          const result = await verifyWithPassword(values.currentPassword ?? '');

          if (result) {
            setPasswordStatus(prevState => ({
              ...(prevState ?? {}),
              status: result,
            }));

            if (result.status === AuthStatus.UNAUTHENTICATED) {
              setErrors([
                {
                  fieldName: 'currentPassword',
                  message: kebabCase(result.message ?? 'unknown-error'),
                },
              ]);
            }
          } else {
            throw new Error(
              'An unknown error has occurred in the server respond.'
            );
          }
        } catch (error) {
          showAlertMessage({
            type: AlertMessageType.ERROR,
            message: (error as Error).message,
          });
        }
      }

      if (action === ProfileModalAction.UPDATEPINCODE) {
        try {
          const values = form.getValues() as InferType<typeof schemas.pincode>;

          const result = await setPinCode(
            values.newPinCode ?? '',
            values.currentPinCode ?? ''
          );

          if (result.success) {
            showAlertMessage({
              type: AlertMessageType.SUCCESS,
              message: intl.formatMessage({
                id: 'profile.profile-update.success',
              }),
            });

            onSaved?.(user!);

            setAction(ProfileModalAction.DEFAULT);
          } else if (result.message) {
            setErrors([
              {
                fieldName: 'currentPinCode',
                message: kebabCase(result.message ?? 'unknown-error'),
              },
            ]);
          } else {
            throw new Error(
              'An unknown error has occurred in the server respond.'
            );
          }
        } catch (error) {
          showAlertMessage({
            type: AlertMessageType.ERROR,
            message: (error as Error).message,
          });
        }
      }

      if (action === ProfileModalAction.UPDATE) {
        const values = form.getValues() as InferType<typeof schemas.default>;

        try {
          const result = await profileUpdate({
            variables: {
              ...values,
              avatar: values.avatar
                ? {
                    fileName: values.avatar.name,
                    base64Data: toUpload(values.avatar.base64),
                  }
                : {
                    deleteFile: true,
                  },
              language: Language.Nl,
            },
          });

          if (result.data?.healthProfessionalUpdateMe?.healthProfessional) {
            reloadUser();

            showAlertMessage({
              type: AlertMessageType.SUCCESS,
              message: intl.formatMessage({
                id: 'profile.profile-update.success',
              }),
            });

            onSaved?.(user!);

            setAction(ProfileModalAction.DEFAULT);
          } else {
            setErrors(result.data?.healthProfessionalUpdateMe?.errors);
          }
        } catch (error) {
          showAlertMessage({
            type: AlertMessageType.ERROR,
            message: (error as Error).message,
          });
        }
      }

      setLoading(false);
    })();
  }, [
    intl,
    form,
    user,
    action,
    profileUpdate,
    reloadUser,
    verifyWithPassword,
    setPinCode,
    showAlertMessage,
    setErrors,
    onSaved,
    setAction,
  ]);

  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);

  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      if (action !== ProfileModalAction.DEFAULT) {
        if (!confirmed && form.formState.isDirty) {
          setCancelPromptOpen(true);
        } else {
          setAction(ProfileModalAction.DEFAULT);
        }
      } else {
        onCancel?.();

        onClose?.();
      }
    },
    [form.formState.isDirty, action, onCancel, onClose]
  );

  const { getFullName, getBirthDate } = useFormatters();

  if (!user) return null;

  return (
    <>
      <Modal
        testId="profile"
        title={
          action !== ProfileModalAction.DEFAULT ? (
            <CustomFormattedMessage
              id={`profile.profile-${kebabCase(action)}.title`}
            />
          ) : (
            <CustomFormattedMessage id="profile.profile.title" />
          )
        }
        width={ModalWidth.LARGE}
        onClose={onHandleCancel}
        buttons={
          <>
            {action === ProfileModalAction.DEFAULT && (
              <>
                <Button
                  inverted
                  onClick={() => setAction(ProfileModalAction.UPDATEPINCODE)}
                >
                  <CustomFormattedMessage id="profile.button.update-pincode" />
                </Button>

                <Button
                  inverted
                  onClick={() => setAction(ProfileModalAction.UPDATEPASSWORD)}
                >
                  <CustomFormattedMessage id="profile.button.update-password" />
                </Button>

                <Button onClick={() => setAction(ProfileModalAction.UPDATE)}>
                  <CustomFormattedMessage id="profile.button.update" />
                </Button>
              </>
            )}

            {action === ProfileModalAction.UPDATE && (
              <>
                <Button
                  type={ButtonType.SECONDARY}
                  inverted
                  onClick={() => onHandleCancel(false)}
                >
                  <CustomFormattedMessage id="common.cancel" />
                </Button>

                <Button
                  icon={loading ? ButtonIcon.LOADING : ButtonIcon.NONE}
                  disabled={!formIsValid || loading}
                  onClick={onHandleSubmit}
                >
                  <CustomFormattedMessage id="profile.profile-update.save" />
                </Button>
              </>
            )}

            {action === ProfileModalAction.UPDATEPASSWORD &&
              (passwordStatus?.status?.status === AuthStatus.UNVERIFIED ? (
                <Button
                  type={ButtonType.SECONDARY}
                  icon={loading ? ButtonIcon.LOADING : ButtonIcon.NONE}
                  disabled={loading}
                  inverted
                  onClick={() => onHandleCancel(false)}
                >
                  <CustomFormattedMessage id="common.cancel" />
                </Button>
              ) : (
                <>
                  <Button
                    type={ButtonType.SECONDARY}
                    inverted
                    onClick={() => onHandleCancel(false)}
                  >
                    <CustomFormattedMessage id="common.cancel" />
                  </Button>

                  <Button
                    icon={loading ? ButtonIcon.LOADING : ButtonIcon.NONE}
                    disabled={!formIsValid || loading}
                    onClick={onHandleSubmit}
                  >
                    <CustomFormattedMessage id="profile.button.update-password" />
                  </Button>
                </>
              ))}

            {action === ProfileModalAction.UPDATEPINCODE && (
              <>
                <Button
                  type={ButtonType.SECONDARY}
                  inverted
                  onClick={() => onHandleCancel(false)}
                >
                  <CustomFormattedMessage id="common.cancel" />
                </Button>

                <Button
                  icon={loading ? ButtonIcon.LOADING : ButtonIcon.NONE}
                  disabled={!formIsValid || loading}
                  onClick={onHandleSubmit}
                >
                  <CustomFormattedMessage id="profile.button.update-pincode" />
                </Button>
              </>
            )}
          </>
        }
      >
        {action === ProfileModalAction.DEFAULT && (
          <>
            <h3>{getFullName(user)}</h3>

            <h4>
              <CustomFormattedMessage id="common.email" />
            </h4>
            <p>
              {user.email && !isEmptyOrNone(user.email) ? (
                <a href={`mailto:${user.email}`} title={user.email}>
                  {user.email}
                </a>
              ) : (
                '-'
              )}
            </p>

            <h4>
              <CustomFormattedMessage id="common.birthdate" />
            </h4>
            <p>
              {!isEmptyOrNone(user.birthDate) ? (
                <CopyText text={formatDate(user.birthDate, 'dd-mm-YYYY')}>
                  {getBirthDate(user)}
                </CopyText>
              ) : (
                '-'
              )}
            </p>

            <h4>
              <CustomFormattedMessage id="common.phone" />
            </h4>
            <p>{!isEmptyOrNone(user.phoneNumber) ? user.phoneNumber : '-'}</p>
          </>
        )}

        {action === ProfileModalAction.UPDATE && (
          <FormProvider {...form}>
            <FormColumns>
              <FormRow width="100px">
                <AvatarUploadController
                  name="avatar"
                  fileTypes={FileTypeImage}
                  fileSize={FileSizeImage}
                />
              </FormRow>

              <FormRow>
                <InputFieldController
                  name="firstName"
                  label={
                    <>
                      <CustomFormattedMessage id="common.firstname" />
                      <small>*</small>
                    </>
                  }
                  placeholder={intl.formatMessage({ id: 'common.firstname' })}
                />
              </FormRow>

              <FormRow>
                <InputFieldController
                  name="lastName"
                  label={
                    <>
                      <CustomFormattedMessage id="common.lastname" />
                      <small>*</small>
                    </>
                  }
                  placeholder={intl.formatMessage({ id: 'common.lastname' })}
                />
              </FormRow>
            </FormColumns>

            <FormRow>
              <InputFieldController
                name="email"
                label={
                  <>
                    <CustomFormattedMessage id="common.email" />
                    <small>*</small>
                  </>
                }
                placeholder={intl.formatMessage({ id: 'common.email' })}
                disabled
              />
            </FormRow>

            <FormRow>
              <InputCheckboxController<Gender>
                type={InputCheckboxType.RADIO}
                name="gender"
                label={
                  <>
                    <CustomFormattedMessage id="common.gender" />
                    <small>*</small>
                  </>
                }
                options={genderData}
              />
            </FormRow>

            <FormRow>
              <InputFieldController
                icon={BaseInputIcon.DATE}
                type="date"
                name="birthDate"
                label={
                  <>
                    <CustomFormattedMessage id="common.birthdate" />
                    <small>*</small>
                  </>
                }
                max={maxBirthDate}
              />
            </FormRow>

            <FormRow>
              <InputFieldController
                type="phone"
                name="phoneNumber"
                label={
                  <>
                    <CustomFormattedMessage id="common.phone" />
                    <small>*</small>
                  </>
                }
                placeholder={intl.formatMessage({
                  id: 'common.phone',
                })}
                disabled
              />
            </FormRow>
          </FormProvider>
        )}

        {action === ProfileModalAction.UPDATEPASSWORD &&
          (passwordStatus?.status?.status === AuthStatus.UNVERIFIED ? (
            <div className="update-password-modal-code">
              <div className="update-password-modal-code__content">
                {passwordStatus?.status?.verification?.phoneNumber && (
                  <h3>
                    <CustomFormattedMessage
                      id="auth.pincode.sms"
                      values={{
                        number:
                          passwordStatus?.status?.verification.phoneNumber,
                      }}
                    />
                  </h3>
                )}

                <FormRow>
                  <PincodeInput
                    loading={loading}
                    error={passwordStatus?.error ?? undefined}
                    onComplete={onHandleCode}
                  />
                </FormRow>
              </div>
            </div>
          ) : (
            <FormProvider {...form}>
              <FormRow>
                <InputFieldController
                  type="password"
                  name="currentPassword"
                  label={
                    <>
                      <CustomFormattedMessage id="profile.profile-update-password.current-password" />
                      <small>*</small>
                    </>
                  }
                  placeholder={intl.formatMessage({
                    id: 'profile.profile-update-password.current-password',
                  })}
                />
              </FormRow>

              <FormRow>
                <InputFieldController
                  type="password"
                  name="newPassword"
                  label={
                    <>
                      <CustomFormattedMessage id="profile.profile-update-password.new-password" />
                      <small>*</small>
                    </>
                  }
                  placeholder={intl.formatMessage({
                    id: 'profile.profile-update-password.new-password',
                  })}
                />
              </FormRow>
              <FormRow>
                <InputFieldController
                  type="password"
                  name="repeatNewPassword"
                  label={
                    <>
                      <CustomFormattedMessage id="profile.profile-update-password.new-password-repeat" />
                      <small>*</small>
                    </>
                  }
                  placeholder={intl.formatMessage({
                    id: 'profile.profile-update-password.new-password-repeat',
                  })}
                />
              </FormRow>
            </FormProvider>
          ))}

        {action === ProfileModalAction.UPDATEPINCODE && (
          <FormProvider {...form}>
            <FormRow>
              <InputFieldController
                name="currentPinCode"
                label={
                  <>
                    <CustomFormattedMessage id="profile.update-pincode.current-pincode" />
                    <small>*</small>
                  </>
                }
                placeholder={intl.formatMessage({
                  id: 'profile.update-pincode.current-pincode',
                })}
                maxLength={5}
              />
            </FormRow>

            <FormRow>
              <InputFieldController
                name="newPinCode"
                label={
                  <>
                    <CustomFormattedMessage id="profile.update-pincode.new-pincode" />
                    <small>*</small>
                  </>
                }
                placeholder={intl.formatMessage({
                  id: 'profile.update-pincode.new-pincode',
                })}
                maxLength={5}
              />
            </FormRow>

            <FormRow>
              <InputFieldController
                name="repeatNewPinCode"
                label={
                  <>
                    <CustomFormattedMessage id="profile.update-pincode.new-pincode-repeat" />
                    <small>*</small>
                  </>
                }
                placeholder={intl.formatMessage({
                  id: 'profile.update-pincode.new-pincode-repeat',
                })}
                maxLength={5}
              />
            </FormRow>
          </FormProvider>
        )}
      </Modal>

      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}
    </>
  );
}
